import queryString from 'querystring';
import {
  filtersToQueryObject,
  getStringQuery,
} from '@prisjakt/product-collection-urls';
import {
  PAGETYPE_ROOT,
  PAGETYPE_PRODUCT,
  PAGETYPE_CATEGORY,
  PAGETYPE_CAMPAIGN,
  PAGETYPE_STORE,
  PAGETYPE_BRAND,
  PAGETYPE_OTHER,
  SCH_PAGETYPE_FRONT,
  SCH_PAGETYPE_OBJECT,
  SCH_PAGETYPE_OTHER,
  SCH_PAGETYPE_SECTION,
} from './helpers/pagetype';

export default (state, action) => {
  if (!state)
    return {
      pathKey: null,
      pageType: PAGETYPE_OTHER,
      schPageType: SCH_PAGETYPE_OTHER,
      pathChain: [],
      brand: null,
      priceData: null,
      placements: [],
      placementsMap: {},
      view: null,
      siteName: '',
      product: [],
    };

  switch (action.type) {
    case '__DANGEROUS_CONFIG_RECEIVE': {
      const { sitename } = action.market;
      return {
        ...state,
        siteName: sitename.replace('é', 'e'), // LeDénicheur,
      };
    }
    case 'CATEGORY_LAYOUT_CHANGED': {
      return {
        ...state,
        view: `category_layout:${action.mode}`,
      };
    }
    case 'ADS_VIEW_UPDATE': {
      return {
        ...state,
        view: action.view,
      };
    }
    case 'ADS_PATHKEY_UPDATE': {
      return {
        ...state,
        pathKey: action.pathKey,
      };
    }
    case 'ADS_RECEIVE': {
      const placements = action.ads;
      const placementsMap = placements.reduce((result, item) => {
        const category = `${item.isVirtual ? 'v' : ''}${item.categoryId}`;
        const itemKey = `${item.platform}:${item.containerName}:${category}`;
        return {
          ...result,
          [itemKey]: item,
        };
      }, {});
      return {
        ...state,
        placementsMap,
      };
    }
    case 'SHOW_FULL_SEARCH_RESULTS': {
      const { query, pagingInfo } = action;
      const {
        categoriesOffset,
        productsOffset,
        brandsOffset,
        storesOffset,
      } = pagingInfo;
      return {
        ...state,
        pathKey: `search:${query}:${categoriesOffset}:${productsOffset}:${brandsOffset}:${storesOffset}`,
      };
    }
    case 'ROUTE_LOADED': {
      const { data, errors, route, location } = action;

      // Bail out if any error occoured
      if (errors || !route) {
        return state;
      }

      let pageData = {};
      switch (route.name) {
        case 'start':
          pageData = {
            pageType: PAGETYPE_ROOT,
            schPageType: SCH_PAGETYPE_FRONT,
          };
          break;

        case 'product': {
          if (!data?.product) {
            break;
          }
          const { product } = data;
          const priceData =
            product.priceSummary && product.priceSummary.regular;

          const pathChain =
            product.category &&
            product.category.path
              .reduce((acc, curr) => acc.concat(curr.id), [])
              .concat(product.id);

          pageData = {
            pageType: PAGETYPE_PRODUCT,
            schPageType: SCH_PAGETYPE_OBJECT,
            pathChain,
            brand: product.brand,
            priceData,
            view: route.name,
            product,
          };
          break;
        }

        case 'productCollection': {
          if (!data?.productCollection) {
            break;
          }
          const { productCollection } = data;
          const adsSlice = productCollection.slices.find(
            ({ __typename }) => __typename === 'AdsSlice'
          );
          const qs = queryString.decode(
            location && location.search ? location.search.substr(1) : ''
          );
          const { categoryPath } = adsSlice || { categoryPath: [] };
          pageData = {
            pageType: PAGETYPE_CATEGORY,
            schPageType: SCH_PAGETYPE_SECTION,
            pathChain: categoryPath,
            pathKey: `${getStringQuery(
              filtersToQueryObject(productCollection.filters)
            )}:${qs.offset}`,
          };
          break;
        }

        case 'keywordLandingPage': {
          const { keywordLandingPage } = data;

          pageData = {
            pageType: PAGETYPE_CATEGORY,
            schPageType: SCH_PAGETYPE_SECTION,
            pathChain:
              keywordLandingPage.parentCategory?.path?.map(p => p.id) ?? [],
          };
          break;
        }

        case 'category': {
          if (!data?.category) {
            break;
          }
          const { category } = data;

          const pathChain =
            category &&
            category.path.reduce((acc, curr) => acc.concat(curr.id), []);

          if (category.filterId) {
            pathChain.push(category.filterId);
          }

          pageData = {
            pageType: PAGETYPE_CATEGORY,
            schPageType: SCH_PAGETYPE_SECTION,
            pathChain,
            brand: category.brand,
          };
          break;
        }
        case 'campaign': {
          const qs = queryString.decode(
            location && location.search ? location.search.substr(1) : ''
          );
          pageData = {
            pathKey: [
              'campaign-page',
              `cat:${qs.category || ''}`,
              `offset:${qs.offset || ''}`,
              `query:${qs.query || ''}`,
            ].join(' '),
            pathChain: (qs.category || '').split('|'),
            pageType: PAGETYPE_CAMPAIGN,
            schPageType: SCH_PAGETYPE_SECTION,
          };
          break;
        }
        case 'store': {
          pageData = { pageType: PAGETYPE_STORE };
          break;
        }
        case 'brand': {
          pageData = { pageType: PAGETYPE_BRAND };
          break;
        }
        case 'search': {
          const qs = queryString.decode(
            location && location.search ? location.search.substr(1) : ''
          );
          const {
            q,
            categoriesOffset,
            productsOffset,
            brandsOffset,
            storesOffset,
          } = qs;
          const { query } = data?.search || {};

          pageData = {
            pathKey: `search:${q || ''}:${categoriesOffset ||
              0}:${productsOffset || 0}:${brandsOffset || 0}:${storesOffset ||
              0}`,
            searchQuery: query,
          };
          break;
        }
        case 'services': {
          pageData = {
            pageType: PAGETYPE_CATEGORY,
            schPageType: SCH_PAGETYPE_SECTION,
            pathChain: [data.categoryTree[0].id || ''],
          };
          break;
        }
        default:
          pageData = { pageType: PAGETYPE_OTHER };
          break;
      }

      return {
        ...state,
        priceData: null,
        pathChain: [],
        pathKey: location.key,
        brand: null,
        ...pageData,
      };
    }
    default:
  }

  return state;
};
